// 定义日志条目接口
interface LogEntry {
  title: string;
  content?: any;
  timestamp?: string;
}

// 配置选项
interface SSLogOptions {
  wait?: number;          // 防抖等待时间（毫秒）
  maxWait?: number;       // 最大等待时间（毫秒）
  maxItems?: number;      // 最大缓存条目数
  serverUrl?: string; // 添加服务器URL配置
}

class SSLogManager {
  private queue: LogEntry[] = [];
  private timeoutId: NodeJS.Timeout | null = null;
  private lastFlushTime: number = 0;
  private isSending: boolean = false;
  
  // 默认配置
  private config: Required<SSLogOptions> = {
    wait: 1000,
    maxWait: 5000,
    maxItems: 10,
    // serverUrl: 'http://localhost:9000/api/logs' // 默认值
    serverUrl: process.env.NEXT_PUBLIC_SSLOG_URL || `${process.env.NEXT_PUBLIC_BASE_URL}/api/user/debug`, // 默认值
  };

  constructor(options?: SSLogOptions) {
    this.config = { ...this.config, ...options };
  }

  // 添加日志到队列
  add(title: string, content?: any): void {
    const entry: LogEntry = {
      title:`${new Date(Date.now()).toLocaleString()}-${title}`,
      content:JSON.stringify(content),
      // timestamp: new Date(Date.now()).toLocaleString(),
    };
    
    this.queue.push(entry);
    
    // 如果超过最大条目数，立即发送
    if (this.queue.length >= this.config.maxItems) {
      this.flush();
      return;
    }
    
    // 设置防抖
    this.debouncedFlush();
    
    // 设置节流（确保至少每maxWait毫秒发送一次）
    if (Date.now() - this.lastFlushTime > this.config.maxWait) {
      this.flush();
    }
  }

  // 防抖实现
  private debouncedFlush(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
    
    this.timeoutId = setTimeout(() => {
      this.flush();
    }, this.config.wait);
  }

  // 发送日志到服务端
  private async flush(): Promise<void> {
    if (this.isSending || this.queue.length === 0) {
      return;
    }
    
    // 清除定时器
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    
    this.isSending = true;
    
    // 复制当前队列并清空
    const logs = [...this.queue];
    this.queue = [];
    const url = this.config.serverUrl;

    try {
      // 发送到服务端
      // const url = 'http://localhost:9000/api/logs';// '/api/user/debug';
      // 使用配置的URL
      await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(logs),

        mode: 'cors', // 明确指定模式
        credentials: 'omit' // 不需要发送凭据
      });
      
      console.log(`成功发送 ${logs.length} 条日志`);
    } catch (error) {
      console.error('日志发送失败:', error);
      console.error('URL:', url);
      console.error('请求体:', { logs: logs });
      // 检查是否是CORS错误
      if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
        console.error('可能是CORS问题，请确保服务器已配置CORS');
      }

      // 如果发送失败，将日志重新放回队列（除了最老的一条）
      this.queue = [...logs.slice(1), ...this.queue];
    } finally {
      this.isSending = false;
      this.lastFlushTime = Date.now();
      
      // 如果队列中还有日志，继续处理
      if (this.queue.length > 0) {
        this.debouncedFlush();
      }
    }
  }
}

// 创建单例实例
const sslogManager = new SSLogManager();

// 导出优化后的函数
export const sslog = (title: string, content?: any) => {
  if(process.env.NODE_ENV === 'production') {
    return;
  }
  
  console.log('sslog:', title, content);
  if(title.startsWith('COPY') && !title.startsWith('mm-onUpdateContent')  &&  process.env.NODE_ENV === 'development') {
    sslogManager.add(title, content);
  }
};

// 导出带配置的函数
export const createSSLog = (options?: SSLogOptions) => {
  const manager = new SSLogManager(options);
  return (title: string, content?: any) => {
    console.log('sslog:', title, content);
    manager.add(title, content);
  };
};